import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FaDownload,
    FaSearch,
    FaSyncAlt,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
} from "react-icons/fa";

import { pdf } from "@react-pdf/renderer";
import Swal from "sweetalert2";

import api from "../api/api";
import EntrancePapperPDF from "./EntrancePapperPDF";

const PAGE_SIZE = 20;

const BRAND = {
    navy: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    orange: "#C0750D",
    green: "#4FAE22",
    red: "#C62828",
    white: "#FFFFFF",
    gray: "#64748B",
    light: "#F4F7FA",
    border: "#DCE3EA",
};

const API_URL = "/api/entrance_examination/admin/papers/";

const EntrancePapper = () => {
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);
    const [error, setError] = useState("");

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);
    const [next, setNext] = useState(null);
    const [previous, setPrevious] = useState(null);

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(count / PAGE_SIZE));
    }, [count]);

    const loadApplicants = useCallback(
        async (requestedPage = 1, requestedSearch = "") => {
            setLoading(true);
            setError("");

            try {
                const response = await api.get(API_URL, {
                    params: {
                        page: requestedPage,
                        page_size: PAGE_SIZE,
                        search: requestedSearch,
                    },
                });

                const data = response?.data || {};

                const results = Array.isArray(data.results)
                    ? data.results
                    : Array.isArray(data.data)
                    ? data.data
                    : Array.isArray(data)
                    ? data
                    : [];

                setApplicants(results);
                setCount(Number(data.count || results.length || 0));
                setNext(data.next || null);
                setPrevious(data.previous || null);
            } catch (requestError) {
                console.error(
                    "Entrance applicants loading failed:",
                    requestError
                );

                setApplicants([]);
                setCount(0);
                setNext(null);
                setPrevious(null);

                setError(
                    requestError?.response?.data?.detail ||
                        requestError?.response?.data?.message ||
                        "Unable to load entrance applicants."
                );
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const trimmedSearch = searchInput.trim();

            setPage(1);
            setSearch(trimmedSearch);
        }, 400);

        return () => window.clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        loadApplicants(page, search);
    }, [page, search, loadApplicants]);

    const downloadPaper = async (applicant) => {
        const applicationNumber =
            applicant?.application_number;

        if (!applicationNumber) {
            Swal.fire({
                icon: "error",
                title: "Missing Application Number",
                text: "This applicant does not have a valid application number.",
                confirmButtonColor: BRAND.navy,
            });

            return;
        }

        setDownloading(applicationNumber);

        try {
            const response = await api.get(
                `${API_URL}${encodeURIComponent(
                    applicationNumber
                )}/`
            );

            const data = response?.data || {};

            const blob = await pdf(
                <EntrancePapperPDF data={data} />
            ).toBlob();

            const url = window.URL.createObjectURL(blob);

            const anchor = document.createElement("a");

            anchor.href = url;
            anchor.download = `Entrance-Paper-${applicationNumber}.pdf`;

            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();

            window.URL.revokeObjectURL(url);
        } catch (requestError) {
            console.error(
                "Entrance paper download failed:",
                requestError
            );

            Swal.fire({
                icon: "error",
                title: "Download Failed",
                text:
                    requestError?.response?.data?.detail ||
                    requestError?.response?.data?.message ||
                    "The entrance paper could not be generated.",
                confirmButtonColor: BRAND.navy,
            });
        } finally {
            setDownloading(null);
        }
    };

    const getAttempt = (applicant, subject) => {
        return applicant?.[subject] || null;
    };

    const getAttemptStatus = (applicant, subject) => {
        return getAttempt(applicant, subject)?.status;
    };

    const renderStatus = (applicant, subject) => {
        const status = getAttemptStatus(
            applicant,
            subject
        );

        if (status === "submitted") {
            return (
                <span
                    style={{
                        color: BRAND.green,
                        fontWeight: 700,
                    }}
                >
                    <FaCheckCircle
                        style={{
                            marginRight: 5,
                        }}
                    />
                    Submitted
                </span>
            );
        }

        if (status === "expired") {
            return (
                <span
                    style={{
                        color: BRAND.red,
                        fontWeight: 700,
                    }}
                >
                    <FaTimesCircle
                        style={{
                            marginRight: 5,
                        }}
                    />
                    Expired
                </span>
            );
        }

        if (status === "in_progress") {
            return (
                <span
                    style={{
                        color: BRAND.orange,
                        fontWeight: 700,
                    }}
                >
                    <FaClock
                        style={{
                            marginRight: 5,
                        }}
                    />
                    In Progress
                </span>
            );
        }

        return (
            <span
                style={{
                    color: BRAND.gray,
                }}
            >
                Not Taken
            </span>
        );
    };

    const getScoreDisplay = (applicant, scoreKey, attemptKey) => {
        const score = applicant?.[scoreKey];

        if (score === null || score === undefined) {
            return "—";
        }

        const totalMarks =
            applicant?.[attemptKey]?.total_marks ??
            applicant?.[attemptKey]?.exam_total_marks ??
            applicant?.[attemptKey]?.total ??
            "";

        return totalMarks !== ""
            ? `${score}/${totalMarks}`
            : `${score}`;
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                background: BRAND.light,
                padding: 20,
            }}
        >
            <style>
                {`
                    * {
                        box-sizing: border-box;
                    }

                    @keyframes entrancePaperSpin {
                        from {
                            transform: rotate(0deg);
                        }

                        to {
                            transform: rotate(360deg);
                        }
                    }

                    .entrance-paper-page {
                        width: 100%;
                    }

                    .entrance-paper-card {
                        background: #fff;
                        border: 1px solid ${BRAND.border};
                        border-radius: 14px;
                        overflow: hidden;
                        box-shadow: 0 5px 18px rgba(6, 42, 75, 0.07);
                    }

                    .entrance-paper-table {
                        width: 100%;
                        border-collapse: collapse;
                        min-width: 1050px;
                    }

                    .entrance-paper-table th {
                        background: ${BRAND.navy};
                        color: #fff;
                        padding: 14px 12px;
                        text-align: left;
                        font-size: 13px;
                        white-space: nowrap;
                    }

                    .entrance-paper-table td {
                        padding: 13px 12px;
                        border-bottom: 1px solid ${BRAND.border};
                        font-size: 13px;
                        vertical-align: middle;
                    }

                    .entrance-paper-table tbody tr:hover {
                        background: #f8fafc;
                    }

                    .entrance-search {
                        width: 100%;
                        max-width: 420px;
                        border: 1px solid ${BRAND.border};
                        border-radius: 9px;
                        padding: 11px 14px 11px 40px;
                        outline: none;
                        font-size: 14px;
                        background: #fff;
                    }

                    .entrance-search:focus {
                        border-color: ${BRAND.blue};
                        box-shadow: 0 0 0 3px rgba(6, 58, 107, 0.08);
                    }

                    .entrance-download {
                        border: none;
                        border-radius: 8px;
                        background: ${BRAND.navy};
                        color: #fff;
                        padding: 8px 11px;
                        cursor: pointer;
                        font-weight: 700;
                        display: inline-flex;
                        align-items: center;
                        gap: 7px;
                        transition: background 0.2s ease;
                    }

                    .entrance-download:hover {
                        background: ${BRAND.blue};
                    }

                    .entrance-download:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }

                    .entrance-pagination {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 12px;
                        padding: 15px;
                        flex-wrap: wrap;
                    }

                    .entrance-page-button {
                        border: 1px solid ${BRAND.border};
                        background: #fff;
                        color: ${BRAND.navy};
                        border-radius: 7px;
                        padding: 8px 13px;
                        cursor: pointer;
                        font-weight: 700;
                    }

                    .entrance-page-button:hover:not(:disabled) {
                        background: #f1f5f9;
                        border-color: ${BRAND.blue};
                    }

                    .entrance-page-button:disabled {
                        opacity: 0.4;
                        cursor: not-allowed;
                    }

                    .entrance-refresh-icon {
                        display: inline-flex;
                    }

                    .entrance-refresh-icon.spinning {
                        animation: entrancePaperSpin 1s linear infinite;
                    }

                    @media (max-width: 700px) {
                        .entrance-paper-page {
                            padding: 0 !important;
                        }

                        .entrance-paper-page-header {
                            padding: 18px !important;
                        }

                        .entrance-paper-page-title {
                            font-size: 20px !important;
                        }

                        .entrance-pagination {
                            align-items: flex-start;
                        }
                    }
                `}
            </style>

            <div
                className="entrance-paper-page"
                style={{
                    maxWidth: 1500,
                    margin: "0 auto",
                }}
            >
                {/* Header */}
                <div
                    className="entrance-paper-page-header"
                    style={{
                        background: BRAND.navy,
                        borderRadius: 14,
                        padding: "22px 24px",
                        color: "#fff",
                        marginBottom: 18,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 15,
                            flexWrap: "wrap",
                        }}
                    >
                        <div>
                            <div
                                className="entrance-paper-page-title"
                                style={{
                                    fontSize: 24,
                                    fontWeight: 800,
                                }}
                            >
                                Entrance Papers
                            </div>

                            <div
                                style={{
                                    marginTop: 5,
                                    opacity: 0.82,
                                    fontSize: 13,
                                }}
                            >
                                Applicant examination papers and responses
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                loadApplicants(page, search)
                            }
                            disabled={loading}
                            style={{
                                border:
                                    "1px solid rgba(255,255,255,.25)",
                                background:
                                    "rgba(255,255,255,.1)",
                                color: "#fff",
                                borderRadius: 8,
                                padding: "9px 13px",
                                cursor: loading
                                    ? "not-allowed"
                                    : "pointer",
                                fontWeight: 700,
                                opacity: loading ? 0.7 : 1,
                            }}
                        >
                            <FaSyncAlt
                                className={
                                    loading
                                        ? "entrance-refresh-icon spinning"
                                        : "entrance-refresh-icon"
                                }
                                style={{
                                    marginRight: 7,
                                }}
                            />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Main card */}
                <div className="entrance-paper-card">
                    {/* Search */}
                    <div
                        style={{
                            padding: 16,
                            borderBottom: `1px solid ${BRAND.border}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 15,
                            flexWrap: "wrap",
                        }}
                    >
                        <div
                            style={{
                                position: "relative",
                                flex: 1,
                                minWidth: 240,
                            }}
                        >
                            <FaSearch
                                style={{
                                    position: "absolute",
                                    left: 14,
                                    top: 13,
                                    color: BRAND.gray,
                                }}
                            />

                            <input
                                className="entrance-search"
                                value={searchInput}
                                onChange={(event) =>
                                    setSearchInput(
                                        event.target.value
                                    )
                                }
                                placeholder="Search applicant name or application number..."
                            />
                        </div>

                        <div
                            style={{
                                color: BRAND.gray,
                                fontSize: 13,
                            }}
                        >
                            {count} applicant
                            {count === 1 ? "" : "s"}
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div
                            style={{
                                margin: 15,
                                padding: 13,
                                background: "#FFF1F1",
                                border: "1px solid #F3C1C1",
                                color: BRAND.red,
                                borderRadius: 8,
                            }}
                        >
                            {error}
                        </div>
                    )}

                    {/* Table */}
                    <div
                        style={{
                            overflowX: "auto",
                        }}
                    >
                        <table className="entrance-paper-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Applicant</th>
                                    <th>Application</th>
                                    <th>Mathematics</th>
                                    <th>English</th>
                                    <th>Exam Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            style={{
                                                textAlign: "center",
                                                padding: 50,
                                            }}
                                        >
                                            Loading entrance applicants...
                                        </td>
                                    </tr>
                                ) : applicants.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            style={{
                                                textAlign: "center",
                                                padding: 50,
                                                color: BRAND.gray,
                                            }}
                                        >
                                            No entrance applicants found.
                                        </td>
                                    </tr>
                                ) : (
                                    applicants.map(
                                        (applicant, index) => (
                                            <tr
                                                key={
                                                    applicant.application_number ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    {(page - 1) *
                                                        PAGE_SIZE +
                                                        index +
                                                        1}
                                                </td>

                                                <td>
                                                    <strong>
                                                        {
                                                            applicant.applicant_name
                                                        }
                                                    </strong>
                                                </td>

                                                <td>
                                                    {
                                                        applicant.application_number
                                                    }
                                                </td>

                                                <td>
                                                    {getScoreDisplay(
                                                        applicant,
                                                        "math_score",
                                                        "mathematics"
                                                    )}
                                                </td>

                                                <td>
                                                    {getScoreDisplay(
                                                        applicant,
                                                        "english_score",
                                                        "english"
                                                    )}
                                                </td>

                                                <td>
                                                    <div>
                                                        {renderStatus(
                                                            applicant,
                                                            "mathematics"
                                                        )}
                                                    </div>

                                                    <div
                                                        style={{
                                                            marginTop: 5,
                                                        }}
                                                    >
                                                        {renderStatus(
                                                            applicant,
                                                            "english"
                                                        )}
                                                    </div>
                                                </td>

                                                <td>
                                                    <button
                                                        type="button"
                                                        className="entrance-download"
                                                        disabled={
                                                            downloading ===
                                                            applicant.application_number
                                                        }
                                                        onClick={() =>
                                                            downloadPaper(
                                                                applicant
                                                            )
                                                        }
                                                    >
                                                        {downloading ===
                                                        applicant.application_number ? (
                                                            <>
                                                                <FaSyncAlt className="entrance-refresh-icon spinning" />
                                                                PDF...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaDownload />
                                                                Download
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="entrance-pagination">
                        <div
                            style={{
                                color: BRAND.gray,
                                fontSize: 13,
                            }}
                        >
                            Page{" "}
                            <strong>{page}</strong>{" "}
                            of{" "}
                            <strong>{totalPages}</strong>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: 8,
                            }}
                        >
                            <button
                                type="button"
                                className="entrance-page-button"
                                disabled={
                                    !previous ||
                                    page <= 1 ||
                                    loading
                                }
                                onClick={() =>
                                    setPage((value) =>
                                        Math.max(
                                            1,
                                            value - 1
                                        )
                                    )
                                }
                            >
                                Previous
                            </button>

                            <button
                                type="button"
                                className="entrance-page-button"
                                disabled={
                                    !next ||
                                    page >= totalPages ||
                                    loading
                                }
                                onClick={() =>
                                    setPage((value) =>
                                        Math.min(
                                            totalPages,
                                            value + 1
                                        )
                                    )
                                }
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EntrancePapper;

